import localforage from 'localforage'
import { computed, ComputedRef, Ref, ref } from 'vue'
import { ELoginResultType } from '@/types/enums/auth'
import { useApi } from './useApi'

const token: Ref<string> = ref('')
const loading: Ref<boolean> = ref(false)
const carrier: Ref<boolean> = ref(false)
const namespace = `${process.env.PACKAGE_NAME || 'app'}`

const localForageUserDataName = 'userData'

type UserData = {
    isCarrier: boolean
}

export const useAuth = (): {
    login: (
        email: string,
        password: string,
    ) => Promise<{ type: ELoginResultType | null; passwordResetToken?: string | null }>
    logout: () => Promise<void>
    token: Ref<string>
    loading: Ref<boolean>
    isLoggedIn: ComputedRef<boolean>
    isCarrier: ComputedRef<boolean>
    checkTokenValidity: () => Promise<void>
    resetPassword: (email: string) => Promise<void>
    resetPasswordStep2: (token: string, password: string, passwordRepeat: string) => Promise<void>
    fetchUserData: () => void
} => {
    const { usePost, readTokenFromStorage } = useApi()
    const login = async (email: string, password: string) => {
        loading.value = true
        let type: ELoginResultType | null = null
        await logout()
        try {
            const result = await usePost('/users/login', {
                email,
                passwordHash: password,
                longTime: process.env.VUE_APP_MODE === 'production' ? 0 : 1,
            })
            if (result.data.data.passwordExpired) {
                const passwordResetToken = result.data.data.passwordResetToken

                return Promise.resolve({
                    type: ELoginResultType.PASSWORD_EXPIRED,
                    passwordResetToken,
                })
            }
            if (!result.data.data.accessToken?.token) {
                throw Error('No authorization token')
            }
            token.value = result.data.data.accessToken.token
            type = ELoginResultType.SUCCESS
            await localforage.setItem(`${namespace}.token`, token.value)

            carrier.value = result.data.data.user.isCarrier
            await localforage.setItem(`${namespace}.${localForageUserDataName}`, {
                isCarrier: carrier.value,
            })
        } catch (error) {
            if (error instanceof Error) {
                throw Error(error.message)
            }
        } finally {
            loading.value = false
        }
        return Promise.resolve({ type })
    }
    const logout = async () => {
        await localforage.removeItem(`${namespace}.token`)
        token.value = ''
    }
    const resetPassword = async (email: string) => {
        loading.value = true
        try {
            await usePost('/users/request-password-reset', { email })
        } catch (error) {
            if (error instanceof Error) {
                throw Error(error.message)
            }
        } finally {
            loading.value = false
        }
    }
    const resetPasswordStep2 = async (token: string, password: string, passwordRepeat: string) => {
        loading.value = true
        try {
            await usePost('/users/password-reset', {
                token,
                password,
                passwordRepeat,
            })
        } catch (error) {
            if (error instanceof Error) {
                throw Error(error.message)
            }
        } finally {
            loading.value = false
        }
    }
    const checkTokenValidity = async () => {
        token.value = await readTokenFromStorage()
    }

    const fetchUserData = async () => {
        const localForageUserData = (await localforage.getItem(
            `${namespace}.${localForageUserDataName}`,
        )) as UserData
        carrier.value = localForageUserData.isCarrier
    }
    return {
        login,
        logout,
        token,
        loading,
        isCarrier: computed(() => carrier.value),
        isLoggedIn: computed(() => !!token.value),
        checkTokenValidity,
        resetPassword,
        resetPasswordStep2,
        fetchUserData,
    }
}
